import { useParams, useSearchParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

type ChartTooltipPayload = { name?: string; value?: number; dataKey?: string };
type ChartTooltipProps = { active?: boolean; payload?: ChartTooltipPayload[]; label?: string };
import { usePropertyDetailQuery } from "@/hooks/usePropertyDetailQuery";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { Typography } from "@/components/ui/Typography";

function formatTimeAgo(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  return `${hr} hr ago`;
}

function InfoTooltip({ label, content }: { label: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={content}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200/80 dark:bg-slate-600/80 text-slate-500 dark:text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
      >
        <span className="text-[10px] font-bold leading-none">?</span>
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-0 top-6 z-10 w-48 rounded-lg border border-slate-200 bg-slate-800 px-2.5 py-1.5 text-xs text-white shadow-lg"
        >
          {content}
        </span>
      )}
    </span>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CHART_PALETTE = [
  "var(--color-primary-500)",
  "#0d9488",
  "var(--color-success-500)",
  "var(--color-warning-500)",
  "#f43f5e",
  "#8b5cf6",
];

const PAY_GRADIENT_ID = "barGradientPaid";
const AREA_GRADIENT_ID = "areaGradientPaid";
const EXPECTED_COLOR = "#e2e8f0";

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl bg-slate-900/95 px-4 py-3 shadow-xl backdrop-blur-sm border-0">
      {label && <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">{label}</p>}
      <div className="space-y-1.5">
        {payload.map((entry: ChartTooltipPayload) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-5">
            <span className="text-sm text-slate-300">{entry.name}</span>
            <span className="text-sm font-bold text-white tabular-nums">
              ₹{typeof entry.value === "number" ? entry.value.toLocaleString("en-IN") : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExpenseTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const value = item.value as number;
  const name = item.name;
  return (
    <div className="rounded-2xl bg-slate-900/95 px-4 py-3 shadow-xl backdrop-blur-sm border-0">
      <p className="text-sm text-slate-300">{name}</p>
      <p className="text-lg font-bold text-white tabular-nums mt-0.5">₹{value.toLocaleString("en-IN")}</p>
    </div>
  );
}

export function PropertyAnalyticsPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = params.id as string;
  const unitId = searchParams.get("unitId") ?? null;
  const [compareOpen, setCompareOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { property, rentRecords, expenses, loading, error, dataUpdatedAt } = usePropertyDetailQuery(id);

  const rentLastYearQuery = useQuery({
    queryKey: [...queryKeys.propertyDetail(id), "rent", lastYear, unitId ?? ""],
    queryFn: () =>
      api.get<{ records: { month: number; year: number; amount: number; status: string; unitId?: string; unitAlias?: string; unitNumber?: string }[] }>(
        `/rent?propertyId=${id}&year=${lastYear}${unitId ? `&roomId=${unitId}` : ""}`
      ).then((r) => r.records),
    enabled: !!id,
  });

  const lastYearFrom = `${lastYear}-01-01T00:00:00.000Z`;
  const lastYearTo = `${lastYear}-12-31T23:59:59.999Z`;
  const expensesLastYearQuery = useQuery({
    queryKey: queryKeys.propertyExpenses(id, lastYearFrom, lastYearTo),
    queryFn: () =>
      api.get<{ expenses: { category: string; amount: number }[] }>(`/expenses?propertyId=${id}&from=${lastYearFrom}&to=${lastYearTo}`).then((r) => r.expenses),
    enabled: !!id && selectedYear === lastYear,
  });

  // All derived data and hooks must run unconditionally (before any early return) so hook count is stable on refresh.
  const recordsThisYear = rentRecords ?? [];
  const recordsLastYear = rentLastYearQuery.data ?? [];
  const filterByUnitId = <T extends { unitId?: string; roomId?: string }>(arr: T[]): T[] =>
    unitId ? arr.filter((r) => (r.unitId ?? r.roomId) === unitId) : arr;
  const recordsThisYearFiltered = filterByUnitId(recordsThisYear);
  const recordsLastYearFiltered = filterByUnitId(recordsLastYear);
  const recordsForYear = selectedYear === currentYear ? recordsThisYearFiltered : recordsLastYearFiltered;
  const expensesForSelectedYearRaw = selectedYear === currentYear ? (expenses ?? []) : (expensesLastYearQuery.data ?? []);
  type ExpenseItem = { category: string; amount: number; unitId?: string | null };
  const expensesForSelectedYear = unitId
    ? (expensesForSelectedYearRaw as ExpenseItem[]).filter((e) => e.unitId === unitId)
    : expensesForSelectedYearRaw;
  const expensesSectionLoading = selectedYear === lastYear && expensesLastYearQuery.isFetching;

  const rentByMonthForYear = useMemo(
    () =>
      MONTHS.map((label, i) => {
        const month = i + 1;
        const monthRecords = recordsForYear.filter((r) => r.month === month);
        const paid = monthRecords.filter((r) => r.status === "PAID").reduce((s, r) => s + r.amount, 0);
        const expected = monthRecords.reduce((s, r) => s + r.amount, 0);
        return { month: label, paid, expected, collection: expected > 0 ? Math.round((paid / expected) * 100) : 0 };
      }),
    [recordsForYear]
  );

  const rentByMonthLastYear = MONTHS.map((label, i) => {
    const month = i + 1;
    const monthRecords = recordsLastYear.filter((r) => r.month === month);
    const paid = monthRecords.filter((r) => r.status === "PAID").reduce((s, r) => s + r.amount, 0);
    return { month: `${label} '${String(lastYear).slice(2)}`, paid };
  });

  const expensesByCategory = expensesForSelectedYear.reduce(
    (acc, e) => {
      const cat = e.category.replace("_", " ");
      acc[cat] = (acc[cat] ?? 0) + e.amount;
      return acc;
    },
    {} as Record<string, number>
  );
  const expensePieData = Object.entries(expensesByCategory).map(([name, value]) => ({ name, value }));

  const rentByUnit = (recordsForYear as { unitId?: string; unitAlias?: string; unitNumber?: string; amount: number; status: string }[]).reduce(
    (acc, r) => {
      const key = r.unitId ?? (r.unitAlias ?? r.unitNumber ?? "Unit");
      if (!acc[key]) acc[key] = { paid: 0, expected: 0, label: r.unitAlias ?? r.unitNumber ?? "Unit" };
      acc[key].expected += r.amount;
      if (r.status === "PAID") acc[key].paid += r.amount;
      return acc;
    },
    {} as Record<string, { paid: number; expected: number; label: string }>
  );
  const unitBarData = Object.entries(rentByUnit).map(([, v]) => ({
    unit: v.label,
    paid: v.paid,
    expected: v.expected,
    rate: v.expected > 0 ? Math.round((v.paid / v.expected) * 100) : 0,
  }));

  const totalRentPaid = recordsForYear.filter((r) => r.status === "PAID").reduce((s, r) => s + r.amount, 0);
  const totalRentExpected = recordsForYear.reduce((s, r) => s + r.amount, 0);
  const collectionRate = totalRentExpected > 0 ? Math.round((totalRentPaid / totalRentExpected) * 100) : 0;
  const totalExpenses = expensesForSelectedYear.reduce((s, e) => s + e.amount, 0);

  const hasRentData = rentByMonthForYear.some((m) => m.expected > 0);
  const hasUnitData = unitBarData.length > 0;
  const hasExpenseData = expensePieData.length > 0;
  const hasNoData = !hasRentData && !hasUnitData && !hasExpenseData;
  const rentByMonthCurrentYear = useMemo(
    () =>
      MONTHS.map((_, i) => {
        const month = i + 1;
        const monthRecords = recordsThisYearFiltered.filter((r) => r.month === month);
        const paid = monthRecords.filter((r) => r.status === "PAID").reduce((s, r) => s + r.amount, 0);
        return { month: `${MONTHS[i]} '${String(currentYear).slice(2)}`, paid };
      }),
    [recordsThisYearFiltered]
  );
  const comparisonYear = selectedYear === currentYear ? lastYear : currentYear;
  const comparisonData = selectedYear === currentYear ? rentByMonthLastYear : rentByMonthCurrentYear;
  const hasComparisonData = comparisonData.some((m) => m.paid > 0);

  if (loading || !property) {
    return (
      <div className="space-y-8 animate-fade-in">
        <div className="h-9 w-56 bg-slate-200/80 dark:bg-slate-700/80 rounded-xl animate-pulse" />
        <div className="grid gap-5 sm:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-slate-200/60 dark:bg-slate-700/60 animate-pulse" />
          ))}
        </div>
        <div className="h-80 rounded-2xl bg-slate-200/60 dark:bg-slate-700/60 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/properties" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
          ← Back to properties
        </Link>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-4 animate-fade-in">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500/8 via-primary-50/40 to-transparent dark:from-primary-500/15 dark:via-slate-800/80 dark:to-slate-800/40 p-6 sm:p-8 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none border border-transparent dark:border-slate-700/80">
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-600/90 dark:text-primary-400">Analytics</p>
          <Typography variant="h1" className="mt-1 tracking-tight sm:text-3xl">
            {property.name}
            {unitId && property.units?.find((u) => u.id === unitId) && (
              <span className="text-slate-500 dark:text-slate-400 font-normal text-xl sm:text-2xl ml-2">
                · {property.units.find((u) => u.id === unitId)?.alias ?? property.units.find((u) => u.id === unitId)?.unitNumber ?? "Unit"}
              </span>
            )}
          </Typography>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span className="text-slate-500 dark:text-slate-400 text-sm">Period:</span>
            <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-0.5 shadow-sm" role="tablist" aria-label="Select year">
              <button
                type="button"
                role="tab"
                aria-selected={selectedYear === currentYear}
                onClick={() => setSelectedYear(currentYear)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${selectedYear === currentYear ? "bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"}`}
              >
                {currentYear}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={selectedYear === lastYear}
                onClick={() => setSelectedYear(lastYear)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${selectedYear === lastYear ? "bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 shadow-sm" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"}`}
              >
                {lastYear}
              </button>
            </div>
          </div>
          {dataUpdatedAt > 0 && (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">Updated {formatTimeAgo(Date.now() - dataUpdatedAt)}</p>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="group rounded-3xl bg-white dark:bg-slate-800/95 p-5 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:hover:shadow-none border border-transparent dark:border-slate-700/80">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">
                Rent collected (YTD) <InfoTooltip label="YTD" content="Year to date: total for the selected year" />
              </p>
              <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">₹{totalRentPaid.toLocaleString("en-IN")}</p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">of ₹{totalRentExpected.toLocaleString("en-IN")} expected</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-success-50 dark:bg-success-900/30 text-success-600 dark:text-success-400">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 11.955-60.059 60.061 60.061 0 0 1 11.955 60.059A60.06 60.06 0 0 1 18 18.75m-9-15v15m0-9h.008" />
              </svg>
            </div>
          </div>
        </div>
        <div className="group rounded-3xl bg-white dark:bg-slate-800/95 p-5 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:hover:shadow-none border border-transparent dark:border-slate-700/80">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">
                Collection rate <InfoTooltip label="rate" content="Percentage of expected rent collected so far" />
              </p>
              <p className={`mt-2 text-2xl font-bold tabular-nums ${collectionRate >= 80 ? "text-success-600 dark:text-success-400" : collectionRate >= 50 ? "text-warning-600 dark:text-warning-400" : "text-slate-900 dark:text-slate-100"}`}>
                {collectionRate}%
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">rent collected vs expected</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75Z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="group rounded-3xl bg-white dark:bg-slate-800/95 p-5 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] dark:hover:shadow-none border border-transparent dark:border-slate-700/80">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">Expenses (YTD)</p>
              <p className="mt-2 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">₹{totalExpenses.toLocaleString("en-IN")}</p>
              <p className="mt-0.5 text-xs text-slate-500">for {selectedYear}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659 1.685 2.127 2.127 2.127 1.685 2.128.879 2.128.879 1.686-1.685 2.127-2.127 2.127-1.685 2.128-.879 2.128-.879-1.686-1.685-2.127-2.127-2.127-1.685-2.128-.879z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <section id="rent-by-month" className="rounded-3xl bg-white dark:bg-slate-800/95 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none overflow-hidden border border-transparent dark:border-slate-700/80" aria-labelledby="rent-by-month-heading">
        <div className="px-6 pt-6 pb-2">
          <Typography as="h2" variant="label" id="rent-by-month-heading" className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">Rent collection by month</Typography>
          <p className="mt-0.5 text-xl font-bold text-slate-900 dark:text-slate-100">{selectedYear}</p>
        </div>
        <div className="px-4 sm:px-6 pb-6">
          {hasRentData ? (
            <>
          <div className="h-64 sm:h-72 -mx-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={rentByMonthForYear} margin={{ top: 12, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id={AREA_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-success-500)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="var(--color-success-500)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#cbd5e1", strokeWidth: 1, strokeDasharray: "4 4" }} />
                <Area type="monotone" dataKey="paid" name="Paid" stroke="var(--color-success-500)" strokeWidth={2.5} fill={`url(#${AREA_GRADIENT_ID})`} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-success-500" />
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Rent collected</span>
            </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.25} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75Z" />
                </svg>
              </div>
              <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No rent records for {selectedYear}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Record rent in each unit to see monthly collection here.</p>
              <Link to={`/property/${id}`} className="mt-4 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                Go to property →
              </Link>
            </div>
          )}
        </div>
      </section>

      <section id="rent-by-unit" className="rounded-3xl bg-white dark:bg-slate-800/95 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none overflow-hidden border border-transparent dark:border-slate-700/80" aria-labelledby="rent-by-unit-heading">
        <div className="px-6 pt-6 pb-2">
          <Typography as="h2" variant="label" id="rent-by-unit-heading" className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">Rent paid by unit</Typography>
          <p className="mt-0.5 text-xl font-bold text-slate-900 dark:text-slate-100">{selectedYear}</p>
        </div>
        <div className="px-4 sm:px-6 pb-6">
          {hasUnitData ? (
            <>
            <div className="h-72 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={unitBarData} layout="vertical" margin={{ top: 8, right: 12, left: 8, bottom: 8 }}>
                  <defs>
                    <linearGradient id="unitBarGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="var(--color-primary-400)" stopOpacity={1} />
                      <stop offset="100%" stopColor="var(--color-primary-600)" stopOpacity={0.92} />
                    </linearGradient>
                  </defs>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`} />
                  <YAxis type="category" dataKey="unit" width={72} tick={{ fontSize: 12, fill: "#475569", fontWeight: 500 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(148, 163, 184, 0.04)" }} />
                  <Bar dataKey="paid" name="Paid" fill="url(#unitBarGradient)" radius={[0, 10, 10, 0]} maxBarSize={32} />
                  <Bar dataKey="expected" name="Expected" fill={EXPECTED_COLOR} radius={[0, 10, 10, 0]} maxBarSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap gap-5">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-primary-500" />
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Paid</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-slate-200 dark:bg-slate-600" />
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Expected</span>
              </div>
            </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.25} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75Z" />
                </svg>
              </div>
              <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No rent by unit for {selectedYear}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Add units and record rent to see breakdown by unit.</p>
              <Link to={`/property/${id}`} className="mt-4 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                Go to property →
              </Link>
            </div>
          )}
        </div>
      </section>

      <section id="expenses" className="rounded-3xl bg-white dark:bg-slate-800/95 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none overflow-hidden border border-transparent dark:border-slate-700/80" aria-labelledby="expenses-heading">
        <div className="px-6 pt-6 pb-2">
          <Typography as="h2" variant="label" id="expenses-heading" className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">Expenses by category</Typography>
          <p className="mt-0.5 text-xl font-bold text-slate-900 dark:text-slate-100">{selectedYear}</p>
        </div>
        {expensesSectionLoading && expensePieData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400" aria-hidden />
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading expenses…</p>
          </div>
        ) : hasExpenseData ? (
          <div className="px-4 sm:px-6 pb-6 flex flex-col sm:flex-row items-center gap-8">
            <div className="relative h-56 w-56 shrink-0 sm:h-64 sm:w-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={expensePieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="60%"
                    outerRadius="92%"
                    paddingAngle={1}
                  >
                    {expensePieData.map((_, i) => (
                      <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} stroke="white" strokeWidth={2.5} />
                    ))}
                  </Pie>
                  <Tooltip content={<ExpenseTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">Total</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-slate-100 tabular-nums">₹{totalExpenses.toLocaleString("en-IN")}</p>
                </div>
              </div>
            </div>
            <div className="flex-1 w-full min-w-0 space-y-1.5">
              {expensePieData.map((item, i) => (
                <div key={item.name} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length] }} />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{item.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 tabular-nums">₹{item.value.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.25} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659 1.685 2.127 2.127 2.127 1.685 2.128.879 2.128.879 1.686-1.685 2.127-2.127 2.127-1.685 2.128-.879z" />
              </svg>
            </div>
            <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No expenses for {selectedYear}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Add property or unit expenses to see breakdown by category.</p>
            <Link to={`/property/${id}/expenses/new`} className="mt-4 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
              Add expense →
            </Link>
          </div>
        )}
      </section>

      {hasComparisonData && (
        <section className="rounded-3xl bg-white dark:bg-slate-800/95 shadow-[0_2px_20px_rgba(0,0,0,0.06)] dark:shadow-none overflow-hidden border border-transparent dark:border-slate-700/80" aria-labelledby="comparison-heading">
          <button
            type="button"
            onClick={() => setCompareOpen((o) => !o)}
            className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors rounded-t-3xl"
            aria-expanded={compareOpen}
            aria-controls="comparison-content"
            id="comparison-heading"
          >
            <div>
              <Typography as="h2" variant="label" className="text-xs font-medium tracking-wide text-slate-500 dark:text-slate-400">Compare with {comparisonYear}</Typography>
              <p className="mt-0.5 text-xl font-bold text-slate-900 dark:text-slate-100">Monthly rent paid</p>
            </div>
            <span className="text-slate-400 dark:text-slate-500" aria-hidden>
              {compareOpen ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </span>
          </button>
          <div id="comparison-content" hidden={!compareOpen} className={compareOpen ? "block" : "hidden"}>
            <div className="px-4 sm:px-6 pb-6">
              <div className="h-60 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={comparisonData} margin={{ top: 12, right: 8, left: -8, bottom: 0 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 500 }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#cbd5e1", strokeWidth: 1, strokeDasharray: "4 4" }} />
                    <Bar dataKey="paid" name="Paid" fill="var(--color-primary-500)" radius={[8, 8, 8, 8]} maxBarSize={36} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Empty state */}
      {hasNoData && (
        <div className="rounded-3xl bg-slate-50/80 dark:bg-slate-800/50 p-12 text-center shadow-[0_2px_20px_rgba(0,0,0,0.04)] dark:shadow-none border border-transparent dark:border-slate-700/80">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-200/80 dark:bg-slate-700/80">
            <svg className="h-8 w-8 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.25} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75Z" />
            </svg>
          </div>
          <Typography variant="h3" className="mt-4 text-slate-700 dark:text-slate-200">No data yet</Typography>
          <p className="mt-2 max-w-sm mx-auto text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
            Record rent and expenses to see charts and insights here.
          </p>
        </div>
      )}
    </div>
  );
}
